// Глобальный виджет дневной нормы «Сегодня N/M мин» в топбаре (§9).
//
// Источник — ТОТ ЖЕ, что у карточки серии на главной и у шапки воркспейса:
// `useStreakState()` → `todayMinutes` (сумма `user_activity_log` за локальный
// день) + `dailyGoalMinutes` (`streak_state.daily_goal_minutes`).
// Решение U3 / декрет E5: `user_study_load_settings.daily_minutes_cap` — бюджет
// планировщика, в виджете НЕ участвует, и своей процедуры (`progress.getTodayLoad`)
// у виджета нет — иначе появился бы третий источник правды и пользователь видел
// бы «18/90» в топбаре против «18/30» на главной.
//
// Читается из PowerSync — работает офлайн и не добавляет сетевых запросов.

import { MetricPill } from "@studyhub/ui";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useStreakState } from "@/components/streaks/_api";

export interface DailyLoadPillProps {
	/** Компактная форма мобильной шапки: кольцо + число, eyebrow в `sr-only`. */
	compact?: boolean;
	className?: string;
}

export function DailyLoadPill({
	compact = false,
	className,
}: DailyLoadPillProps): React.ReactElement | null {
	const { t } = useTranslation("layout");
	const navigate = useNavigate();
	const { data, isLoading } = useStreakState();

	if (isLoading) {
		return (
			<Skeleton
				className={compact ? "h-9 w-[68px] rounded-lg" : "h-9 w-32 rounded-lg"}
				aria-hidden
			/>
		);
	}
	// Стрика ещё нет (новый аккаунт, локальная БД пуста) — виджет не выдумывает
	// «0/30», а просто не занимает место в шапке.
	if (!data) return null;

	const goal = Math.max(1, data.dailyGoalMinutes);
	const value = compact
		? t("dailyLoad.valueCompact", { done: data.todayMinutes })
		: t("dailyLoad.value", { done: data.todayMinutes, goal: data.dailyGoalMinutes });

	return (
		<MetricPill
			eyebrow={t("dailyLoad.eyebrow")}
			value={value}
			progress={Math.min(1, data.todayMinutes / goal)}
			compact={compact}
			className={className}
			ariaLabel={t("dailyLoad.aria", {
				done: data.todayMinutes,
				goal: data.dailyGoalMinutes,
			})}
			onClick={() => void navigate({ to: "/progress" })}
		/>
	);
}
